import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Inbox, UserCog, LogOut, FileText, ChevronDown, Newspaper, BookOpen, Mail, Users, Contact, Target, Settings as SettingsIcon, Calendar, CalendarCheck, Building2, CreditCard, Handshake, Bell, LifeBuoy, ShieldAlert, Plug } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { inboxFeed } from '../lib/marketingApi';

const FEED_META = {
  enquiry: { label: 'Enquiry', icon: Inbox, cls: 'bg-sky-100 text-sky-700' },
  support: { label: 'Support', icon: LifeBuoy, cls: 'bg-violet-100 text-violet-700' },
  dispute: { label: 'Dispute', icon: ShieldAlert, cls: 'bg-rose-100 text-rose-700' },
  billing: { label: 'Billing', icon: CreditCard, cls: 'bg-amber-100 text-amber-700' },
  general: { label: 'General', icon: Mail, cls: 'bg-slate-100 text-slate-600' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState([]);
  const [seen, setSeen] = useState(() => Number(localStorage.getItem('bp_inbox_seen') || 0));
  const ref = useRef(null);

  const refresh = () => inboxFeed().then((d) => setFeed(d.items || [])).catch(() => {});
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const unread = feed.filter((f) => new Date(f.at || 0).getTime() > seen).length;
  const markSeen = () => { const now = Date.now(); localStorage.setItem('bp_inbox_seen', String(now)); setSeen(now); };
  const toggle = () => { setOpen((v) => { if (!v) markSeen(); return !v; }); };
  const go = (item) => { setOpen(false); navigate(item.link); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" title="Notifications"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF5757] px-1 text-[10px] font-bold text-white">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            <span className="text-xs text-slate-400">{feed.length} new</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {feed.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 py-10 text-slate-400"><Bell className="h-6 w-6" /><p className="text-xs">You're all caught up</p></div>
            ) : feed.map((item) => {
              const m = FEED_META[item.type] || FEED_META.general;
              return (
                <button key={item.id} onClick={() => go(item)} data-testid={`notification-item-${item.id}`}
                  className="flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${m.cls}`}><m.icon className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${m.cls}`}>{m.label}</span>
                      <span className="truncate text-sm font-semibold text-slate-900">{item.title}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{item.subtitle}</div>
                    <div className="mt-0.5 text-[10px] text-slate-400">{timeAgo(item.at)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const TOP_TABS = [
  { key: 'dashboard', label: 'Dashboard', to: '/admin/dashboard', icon: LayoutDashboard },
  { key: 'inbox', label: 'Inbox', to: '/admin/inbox', icon: Inbox },
  { key: 'subscriptions', label: 'App Registrations', to: '/admin/subscriptions', icon: CreditCard },
];

const CRM_CHILDREN = [
  { key: 'companies', label: 'Companies', to: '/admin/companies', icon: Building2 },
  { key: 'contacts', label: 'Contacts', to: '/admin/contacts', icon: Contact },
  { key: 'enquiries', label: 'Enquiries', to: '/admin/enquiries', icon: Inbox },
  { key: 'partners', label: 'Partners', to: '/admin/partners', icon: Handshake },
  { key: 'retargeting', label: 'Retargeting', to: '/admin/retargeting', icon: Target },
];
const CRM_KEYS = CRM_CHILDREN.map((c) => c.key);

const CONTENT_CHILDREN = [
  { key: 'blogs', label: 'Blogs', to: '/admin/content/blogs', icon: Newspaper },
  { key: 'case-studies', label: 'Case Studies', to: '/admin/content/case-studies', icon: BookOpen },
  { key: 'newsletters', label: 'Newsletters', to: '/admin/newsletters', icon: Mail },
  { key: 'subscribers', label: 'Subscribers', to: '/admin/subscribers', icon: Users },
];

const SETTINGS_CHILDREN = [
  { key: 'my-calendar', label: 'My Calendar', to: '/admin/calendar', icon: CalendarCheck, activeWhen: 'my-calendar' },
  { key: 'users', label: 'Users', to: '/admin/users', icon: UserCog, activeWhen: 'users', adminOnly: true },
  { key: 'calendar', label: 'Booking Links', to: '/admin/settings', icon: Calendar, activeWhen: 'settings', adminOnly: true },
  { key: 'tech-config', label: 'Tech Config', to: '/admin/tech-config', icon: Plug, activeWhen: 'tech-config', adminOnly: true },
];

const ROLE_LABEL = { admin: 'Admin', editor: 'Editor', viewer: 'Viewer' };

function Dropdown({ testid, label, icon: Icon, active, children, activeChildKey }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button data-testid={`admin-nav-${testid}`} onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${active ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
        <Icon className="h-4 w-4" /> {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div data-testid={`admin-${testid}-submenu`} className="absolute left-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {children.map((c) => (
            <Link key={c.key} to={c.to} onClick={() => setOpen(false)} data-testid={`admin-nav-${testid}-${c.key}`}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium hover:bg-slate-50 hover:text-[#0035A8] ${(activeChildKey === c.key || activeChildKey === c.activeWhen) ? 'text-[#0035A8]' : 'text-slate-600'}`}>
              <c.icon className="h-4 w-4" /> {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminHeader({ active, actions }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const doLogout = async () => { await logout(); navigate('/admin/login'); };

  const initials = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  const crmActive = CRM_KEYS.includes(active);
  const contentActive = active === 'content';
  const settingsActive = active === 'settings' || active === 'users' || active === 'my-calendar' || active === 'tech-config';
  const settingsChildren = SETTINGS_CHILDREN.filter((c) => !c.adminOnly || isAdmin);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
        <Link to="/admin/dashboard" className="flex items-center gap-2.5">
          <img src="/BP-Mark.png" alt="BillPortal" className="h-8 w-8 object-contain" />
          <span className="text-lg font-bold tracking-tight text-slate-900">BillPortal</span>
          <span className="hidden text-slate-300 sm:inline">/</span>
          <span className="hidden text-sm font-medium text-slate-500 sm:inline">Marketing CRM</span>
        </Link>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <div className="hidden items-center gap-2.5 sm:flex">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0035A8]/10 text-sm font-bold text-[#0035A8]">{initials}</span>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-800">{user?.name || user?.email}</div>
              <div className="text-[11px] font-medium text-[#0035A8]">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-btn" title="Log out"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800">
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      <div className="border-t border-slate-100">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-2.5 sm:px-8">
          <nav data-testid="admin-nav" className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1">
            {TOP_TABS.map((t) => {
              const isActive = active === t.key;
              return (
                <Link key={t.key} to={t.to} data-testid={`admin-nav-${t.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${isActive ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </Link>
              );
            })}

            <Dropdown testid="crm" label="CRM" icon={Users} active={crmActive} activeChildKey={active} children={CRM_CHILDREN} />
            <Dropdown testid="content" label="Content" icon={FileText} active={contentActive} activeChildKey={active} children={CONTENT_CHILDREN} />
            <Dropdown testid="settings" label="Settings" icon={SettingsIcon} active={settingsActive} activeChildKey={active} children={settingsChildren} />
          </nav>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
    </header>
  );
}
